import React, { useCallback, useEffect, useState } from 'react'

import { ADDITIONAL_RESULT_ATTRIBUTE_KEYS, FAQ_MATCHES } from '../constants'
import collapse from '../images/solid-down.svg'
import expand from '../images/solid-right.svg'
import {
  isNullOrEmpty,
  isNullOrUndefined,
  selectMostRecentUpdatedTimestamp
} from '../utils'

import ResultFooter from './components/ResultFooter'
import ResultText from './components/ResultText'
import TextHighlights from './components/TextWithHighlight'

/**
 * Component for displaying KendraFAQ results.
 *
 * @see {@link https://github.com/aws-samples/aws-kendra-transcribe-media-search/blob/main/finderapp/src/search/resultsPanel/FAQResults.tsx FAQResults.tsx - AWS Samples}
 * @see {@link https://github.com/elangovana/kendra-ui-sample/blob/master/src/search/resultsPanel/FAQResults.tsx FAQResults.tsx - Elango Vana}
 * @example
 * const results = [{ ... }]
 * const submitFeedback = (feedbackType, queryResultItem) => { ... }
 * return <FAQResults results={results} submitFeedback={submitFeedback} />
 *
 * @param {Object} props - The component props.
 * @param {Array} props.results - The array of FAQ result items.
 * @param {Function} props.submitFeedback - The function to submit feedback.
 * @returns {JSX.Element|null} The rendered FAQResults component or null if no results are available.
 */
const FAQResults = ({ results, submitFeedback }) => {
  const [faqExpandedMap, setFaqExpandedMap] = useState([])

  /**
   * Initializes the expanded map state when results change.
   */
  useEffect(() => {
    if (!isNullOrEmpty(results)) {
      let expandedMap = []
      for (let i = 0; i < 10; i++) {
        expandedMap.push({ expanded: false })
      }
      setFaqExpandedMap(expandedMap)
    }
  }, [results])

  /**
   * Handles the click event to expand or collapse an FAQ item.
   *
   * @param {Object} event - The click event.
   * @param {number} index - The index of the FAQ item to expand or collapse.
   */
  const handleExpansionClick = useCallback((event, index) => {
    setFaqExpandedMap(prevMap => {
      const newMap = [...prevMap]
      newMap[index] = { expanded: !prevMap[index].expanded }
      return newMap
    })
  }, [])

  /**
   * Renders individual FAQ result items.
   *
   * @param {Object} result - The result item to be rendered.
   * @param {number} index - The index of the result item.
   * @returns {JSX.Element} The rendered result item.
   */
  const renderResults = useCallback(
    (result, index) => {
      let resultAttributes = {}
      if (!isNullOrUndefined(result.AdditionalAttributes)) {
        result.AdditionalAttributes.forEach(attribute => {
          resultAttributes[attribute.Key] = attribute.Value
        })
      }

      const question =
        resultAttributes[ADDITIONAL_RESULT_ATTRIBUTE_KEYS.QUESTION_TEXT]
      const answer =
        resultAttributes[ADDITIONAL_RESULT_ATTRIBUTE_KEYS.ANSWER_TEXT]

      let title
      if (!isNullOrUndefined(question)) {
        title = (
          <div className="faq-question action-link">
            <TextHighlights
              textWithHighlights={question.TextWithHighlightsValue}
            />
          </div>
        )
      }

      let content
      if (!isNullOrUndefined(answer)) {
        content = answer.TextWithHighlightsValue
      }

      let attributes = {}
      if (!isNullOrUndefined(result.DocumentAttributes)) {
        result.DocumentAttributes.forEach(attribute => {
          attributes[attribute.Key] = attribute.Value
        })
      }

      const lastUpdated = selectMostRecentUpdatedTimestamp(attributes)

      return (
        <React.Fragment key={result.Id}>
          <div className="container-divider" />
          <div className="faq-result">
            <div
              className="faq-title"
              onClick={event => handleExpansionClick(event, index)}
            >
              {!isNullOrEmpty(faqExpandedMap) &&
                !faqExpandedMap[index].expanded && (
                  <img
                    alt="expand"
                    className="expand-collapse"
                    src={expand}
                  ></img>
                )}
              {!isNullOrEmpty(faqExpandedMap) &&
                faqExpandedMap[index].expanded && (
                  <img
                    alt="collapse"
                    className="expand-collapse"
                    src={collapse}
                  ></img>
                )}
              <div className="display-inline"> {title}</div>
            </div>
            {!isNullOrEmpty(faqExpandedMap) &&
              faqExpandedMap[index].expanded && (
                <div className="small-left-padding">
                  <div className="container-body faq-answer">
                    <ResultText
                      className="small-margin-bottom"
                      text={content}
                      lastUpdated={lastUpdated}
                    />
                    <ResultFooter
                      queryResultItem={result}
                      attributes={attributes}
                      submitFeedback={submitFeedback}
                    />
                  </div>
                </div>
              )}
          </div>
        </React.Fragment>
      )
    },
    [faqExpandedMap, handleExpansionClick, submitFeedback]
  )

  if (isNullOrUndefined(results) || results.length === 0) {
    return null
  }

  return (
    <div className="result-container card">
      <div className="card-title">{FAQ_MATCHES}</div>
      {results.map(renderResults)}
    </div>
  )
}

export default FAQResults
